import _ from 'lodash';
import Cookies from 'js-cookie';
import { fabric } from 'fabric';
import { Common } from '@src/common';
import animationJSON from '@action/text_animation_info';
import { isNotNull, singleToDoubleByte } from '@action/util';
import { IAnimationInitData } from '@typings';

const animationInitData: IAnimationInitData = {
  lineSetting: {},
  firstPosition: { top: 0, left: 0 },
  secondPosition: { top: 0, left: 0 },
  fontSizeFirstLine: 0,
  fontSizeSecondLine: 0,
  topDiff: 0,
  leftDiff: 0,
  isMultiLine: true,
};

let animationStartPosition;
let animationEndPosition;

let itextFirstLineConfig;
let itextSecondLineConfig;

const setStickerOrder = (data, textInfo) => {
  if (_.size(data.stickers)) {
    if (textInfo.index === 1) {
      if (data.stickers) {
        data.stickers.forEach((item) => data.fb.add(item));
        if (data.isMultiCanvas) {
          data.stickers.forEach((item) => data.fbSecond.add(item));
        }
      }
    }

    if (data.elementOnTop === 'text') {
      data.fb.getObjects().forEach((text) => {
        if (text.type === 'i-text') {
          data.fb.bringToFront(text);
        }
      });
      if (data.isMultiCanvas) {
        data.fbSecond.getObjects().forEach((text) => {
          if (text.type === 'i-text') {
            data.fbSecond.bringToFront(text);
          }
        });
      }
    } else {
      data.fb.getObjects().forEach((text) => {
        if (text.type === 'i-text') {
          data.fb.sendToBack(text);
        }
      });
      if (data.isMultiCanvas) {
        data.fbSecond.getObjects().forEach((text) => {
          if (text.type === 'i-text') {
            data.fbSecond.sendToBack(text);
          }
        });
      }
    }
  }
};

const textConfigSetting = {
  getScaleX(data, textLine, textSizeType) {
    const isVerticalAnimationCondition = data.product_type_id === 'V044391';
    const isPhoneNumber =
      Common.EXP_TYPE_PHONE_INTERNATIONAL.test(textLine) === false;
    const isFontSizeLarge = textSizeType === 'large';
    let unit = 1;

    if (isVerticalAnimationCondition && isPhoneNumber && isFontSizeLarge) {
      unit = 1.3;
    } else if (isVerticalAnimationCondition && isPhoneNumber) {
      unit = 1.5;
    }

    return unit;
  },

  getStrokeWidth(textSizeType, fontSize, fontFamily) {
    let strokeWidth = 0.5;
    if (fontSize >= 38) {
      strokeWidth = textSizeType === 'small' && fontSize >= 38 ? 1.5 : 2;
    }
    if (fontFamily === 'HGRGE' || fontFamily === 'MeiryoBold') {
      strokeWidth = textSizeType === 'small' ? 0.3 : 0.6;
    } else {
      strokeWidth = textSizeType === 'small' ? 0.5 : 0.8;
    }

    return strokeWidth;
  },

  getLineHeight(textSizeType, data, textInfo, lineHeight = 1.16) {
    if (data.isVerticalAnimationCondition) {
      lineHeight = textSizeType === 'small' ? 1 : 1.02;
      if (Cookies.get(Common.STORAGE_LANG) === 'ko') lineHeight = 1;
    }
    return lineHeight;
  },

  getFontSize(fontSize, data, { animationName, textSizeType, textLine }) {
    let fontSizeUnit = 1;

    // "010-0000-0000, +82) 10-0000-0000" 형태의 휴대폰번호를 받으면 글자 크기가 커지도록
    // 숫자와 특수문자 (괄호, 하이픈('-'), +)인지 검사
    if (
      ['H044391', 'V055391', 'S063391'].indexOf(data.product_type_id) !== -1
    ) {
      // 작은 글씨이고, 1x1세로이면서 slide_top, slide_bottom인경우,
      // 또는 1x1세로가 아니면서 slide_left, slide_right인경우
      // 숫자가 있으면 1.5로 키운다.

      const { isVerticalAnimationCondition, product_type_id: productTypeId } =
        data;
      const unitChangeCondition =
        (isVerticalAnimationCondition &&
          (animationName === 'slide_top' ||
            animationName === 'slide_bottom')) ||
        (!isVerticalAnimationCondition &&
          (animationName === 'slide_left' || animationName === 'slide_right'));

      if (unitChangeCondition) {
        fontSizeUnit = 1.5;

        if (productTypeId === 'H044391' && textSizeType === 'large')
          fontSizeUnit = 1.35;
        if (productTypeId === 'V055391' && textSizeType === 'large')
          fontSizeUnit = 1;
      }
      if (
        !Common.EXP_TYPE_PHONE_INTERNATIONAL.test(textLine) &&
        textSizeType === 'small'
      ) {
        fontSizeUnit = 1.5;
      }
    }

    return Math.floor(fontSize * fontSizeUnit);
  },
};

const animationInit = (data, textInfo) => {
  const {
    model,
    h_res: signWidth,
    v_res: signHeight,
    rotate,
  } = data.product_type_info;
  const {
    animation_name: animationName,
    text_size_type: textSizeType,
    text_size_type_second: textSizeTypeSecond,
    first_line: firstLine,
    second_line: secondLine,
    multi_line_yn: multiLineYN,
  } = textInfo;
  const isLangJP = Cookies.get(Common.STORAGE_LANG) === Common.CONST_LANG_JA;

  animationInitData.isMultiLine = multiLineYN === 'Y';
  if (data.isBannerCondition) {
    // 배너 타입 전광판인 경우
    let firstTop;
    let secondTop;
    if (model === 'AOM39101') {
      // 가로 단위가 256이므로 256에 맞는 문구 제한 개수 * 유닛 개수
      if (animationInitData.isMultiLine) {
        firstTop = 38;
        secondTop = 93;
      } else {
        firstTop = 70;
        secondTop = 0;
      }
    } else if (model === 'AOM62501') {
      // 가로 단위가 160이므로 160에 맞는 문구 제한 개수 * 유닛 개수
      if (animationInitData.isMultiLine) {
        firstTop = signHeight * 0.28;
        secondTop = signHeight * 0.75;
      } else {
        firstTop = 45;
        secondTop = 0;
      }
    }
    animationInitData.firstPosition = { top: firstTop, left: signWidth / 2 };
    animationInitData.secondPosition = { top: secondTop, left: signWidth / 2 };
    animationInitData.topDiff = secondTop - firstTop;
    animationInitData.leftDiff = 0;
  } else {
    const idx = data.clicked_text ? data.clicked_text - 1 : 0;
    const getLineSettingCondition = (item) => {
      const multiLineCondition =
        animationInitData.isMultiLine === false
          ? item.multi_line_type === 1
          : item.multi_line_type === 2;
      const commonCondition =
        item.h_res === signWidth && item.v_res === signHeight;

      return multiLineCondition && commonCondition;
    };
    if (data.animation_preset && data.animation_preset[idx]) {
      const item = data.animation_preset[idx];
      if (getLineSettingCondition(item)) animationInitData.lineSetting = item;
    } else {
      animationInitData.lineSetting = animationJSON[model].find(
        (animationInfo) => {
          const multiLineCondition =
            animationInitData.isMultiLine === false
              ? animationInfo.multi_line_type === 1
              : animationInfo.multi_line_type === 2;

          return (
            animationInfo.h_res === signWidth &&
            animationInfo.v_res === signHeight &&
            animationInfo.rotate === rotate &&
            multiLineCondition
          );
        },
      );
      animationInitData.lineSetting.font_size_first = animationInitData
        .lineSetting.font_size[textSizeType][Cookies.get(Common.STORAGE_LANG)]
        ? animationInitData.lineSetting.font_size[textSizeType][
            Cookies.get(Common.STORAGE_LANG)
          ]
        : animationInitData.lineSetting.font_size[textSizeType];
      animationInitData.lineSetting.font_size_second = animationInitData
        .lineSetting.font_size[textSizeTypeSecond][
        Cookies.get(Common.STORAGE_LANG)
      ]
        ? animationInitData.lineSetting.font_size[textSizeTypeSecond][
            Cookies.get(Common.STORAGE_LANG)
          ]
        : animationInitData.lineSetting.font_size[textSizeTypeSecond];
    }
  }

  let settingValueFirst;
  let settingValueSecond;

  if (!data.isBannerCondition) {
    const { text_position: textPosition } = animationInitData.lineSetting;
    if (animationInitData.isMultiLine) {
      settingValueFirst =
        textPosition.first_line[textSizeType][textInfo.textVerticalAlign];
      settingValueSecond =
        textPosition.second_line[textSizeTypeSecond][
          textInfo.textVerticalAlign
        ];
      animationInitData.firstPosition = settingValueFirst;
      animationInitData.secondPosition = settingValueSecond;
      animationInitData.topDiff =
        settingValueSecond.top - settingValueFirst.top;
      animationInitData.leftDiff =
        settingValueSecond.left - settingValueFirst.left;
    } else {
      settingValueFirst =
        textPosition.first_line[textSizeType][textInfo.textVerticalAlign];
      animationInitData.firstPosition = settingValueFirst;
      animationInitData.secondPosition = { top: 0, left: 0 };
      animationInitData.topDiff = 0;
      animationInitData.leftDiff = 0;
    }
  }

  if (!isNotNull(textInfo.animate_first_line)) {
    textInfo.animate_first_line = '';
  }
  if (!isNotNull(textInfo.animate_second_line)) {
    textInfo.animate_second_line = '';
  }
  if (!isNotNull(textInfo.default_text)) {
    textInfo.default_text = '';
  }

  data.fb.remove(...data.fb.getObjects('i-text'));

  if (data.isMultiCanvas) {
    data.fbSecond.remove(...data.fbSecond.getObjects('i-text'));
  }

  if (!data.isBannerCondition) {
    animationInitData.fontSizeFirstLine =
      animationInitData.lineSetting.font_size_first;
    animationInitData.fontSizeSecondLine =
      animationInitData.lineSetting.font_size_second;
  } else {
    animationInitData.fontSizeFirstLine = data.fbText.fontSize;
    animationInitData.fontSizeSecondLine = data.fbTextSecondLine.fontSize;
  }

  let animateFirstText;
  let animateSecondText;

  if (data.isVerticalAnimationCondition) {
    animateFirstText = textInfo.animate_first_line;
    animateSecondText = textInfo.animate_second_line;
  } else if (data.isBannerCondition) {
    animateFirstText = singleToDoubleByte(firstLine);
    animateSecondText = singleToDoubleByte(textInfo.second_line);
  } else {
    animateFirstText = firstLine;
    animateSecondText = textInfo.second_line;
  }

  const itextCommonConfig = {
    fill: textInfo.color_hex,
    stroke: textInfo.border_color_hex,
    selectable: false,
    textAlign: 'center',
    charSpacing: 30,
    fontFamily: isLangJP ? 'MeiryoBold' : 'NotoSansKRBlack',
    lineHeight: 1.16,
  };

  itextFirstLineConfig = {
    ...itextCommonConfig,
    text: animateFirstText,
    scaleX: textConfigSetting.getScaleX(data, firstLine, textSizeType),
    strokeWidth: textConfigSetting.getStrokeWidth(
      textSizeType,
      animationInitData.fontSizeFirstLine,
      itextCommonConfig.fontFamily,
    ),
    lineHeight: textConfigSetting.getLineHeight(
      data.text_size_type,
      data,
      textInfo,
      itextCommonConfig.lineHeight,
    ),
    fontSize: textConfigSetting.getFontSize(
      animationInitData.fontSizeFirstLine,
      data,
      {
        animationName,
        textSizeType,
        // @ts-ignore
        firstLine,
      },
    ),
  };
  itextSecondLineConfig = {
    ...itextCommonConfig,
    text: animateSecondText,
    scaleX: textConfigSetting.getScaleX(data, secondLine, textSizeTypeSecond),
    strokeWidth: textConfigSetting.getStrokeWidth(
      textSizeTypeSecond,
      animationInitData.fontSizeSecondLine,
      textInfo,
    ),
    lineHeight: textConfigSetting.getLineHeight(
      data.text_size_type_second,
      data,
      textInfo,
      itextCommonConfig.lineHeight,
    ),
    fontSize: textConfigSetting.getFontSize(
      animationInitData.fontSizeSecondLine,
      data,
      {
        animationName,
        // @ts-ignore
        textSizeTypeSecond,
        // @ts-ignore
        secondLine,
      },
    ),
  };

  const changeTextOrderCondition =
    data.isVerticalAnimationCondition &&
    Cookies.get(Common.STORAGE_LANG) === Common.CONST_LANG_JA &&
    animationInitData.isMultiLine;

  // 1x1 세로 일본어 멀티 라인 문구 순서 바꿈
  if (changeTextOrderCondition) {
    const newAnimateFirstText = animateFirstText;
    const newAnimateSecondText = animateSecondText;
    const newItextFirstLineConfig = _.cloneDeep(itextFirstLineConfig);
    const newItextSecondLineConfig = _.cloneDeep(itextSecondLineConfig);

    animateFirstText = newAnimateSecondText;
    animateSecondText = newAnimateFirstText;
    itextFirstLineConfig = newItextSecondLineConfig;
    itextSecondLineConfig = newItextFirstLineConfig;
  }

  // 텍스트 인스턴스 생성
  data.fbText = new fabric.IText(animateFirstText, itextFirstLineConfig);
  data.fb.add(data.fbText);
  if (animationInitData.isMultiLine) {
    data.fbTextSecondLine = new fabric.IText(
      animateSecondText,
      itextSecondLineConfig,
    );
    data.fb.add(data.fbTextSecondLine);
  }

  if (data.isMultiCanvas) {
    data.cloneFirstLine = new fabric.IText(
      animateFirstText,
      itextFirstLineConfig,
    );
    data.fbSecond.add(data.cloneFirstLine);
    if (animationInitData.isMultiLine) {
      data.cloneSecondLine = new fabric.IText(
        animateSecondText,
        itextSecondLineConfig,
      );
      data.fbSecond.add(data.cloneSecondLine);
    }
  }

  setStickerOrder(data, textInfo);
};

const setMoveAnimationStartPoint = (data, textInfo) => {
  data.fbText.set({ left: itextFirstLineConfig.left });
  data.fbText.set({ top: itextFirstLineConfig.top });
  data.fbText.set({ originX: itextFirstLineConfig.originX });
  data.fbText.set({ originY: itextFirstLineConfig.originY });
  if (textInfo.multi_line_yn === 'Y') {
    data.fbTextSecondLine.set({ left: itextSecondLineConfig.left });
    data.fbTextSecondLine.set({ top: itextSecondLineConfig.top });
    data.fbTextSecondLine.set({ originX: itextSecondLineConfig.originX });
    data.fbTextSecondLine.set({ originY: itextSecondLineConfig.originY });
  }
  if (data.isMultiCanvas) {
    data.cloneFirstLine.set({ left: itextFirstLineConfig.left });
    data.cloneFirstLine.set({ top: itextFirstLineConfig.top });
    data.cloneFirstLine.set({ originX: itextFirstLineConfig.originX });
    data.cloneFirstLine.set({ originY: itextFirstLineConfig.originY });
    if (textInfo.multi_line_yn === 'Y') {
      data.cloneSecondLine.set({ left: itextSecondLineConfig.left });
      data.cloneSecondLine.set({ top: itextSecondLineConfig.top });
      data.cloneSecondLine.set({ originX: itextSecondLineConfig.originX });
      data.cloneSecondLine.set({ originY: itextSecondLineConfig.originY });
    }
  }
};

const setFixedAnimationStartPoint = (data) => {
  data.fbText.set(itextFirstLineConfig);
  if (animationInitData.isMultiLine) {
    data.fbTextSecondLine.set(itextSecondLineConfig);
  }

  if (data.isMultiCanvas) {
    data.cloneFirstLine.set(itextFirstLineConfig);
    if (animationInitData.isMultiLine) {
      data.cloneSecondLine.set(itextSecondLineConfig);
    }
  }
};

const setAnimationPosition = (
  { first_line: firstLine, second_line: secondLine },
  originX = 'center',
  originY = 'center',
) => {
  itextFirstLineConfig = {
    ...itextFirstLineConfig,
    top: firstLine.top,
    left: firstLine.left,
    originX,
    originY,
  };

  if (!animationInitData.isMultiLine) return;
  itextSecondLineConfig = {
    ...itextSecondLineConfig,
    top: secondLine.top,
    left: secondLine.left,
    originX,
    originY,
  };
};

const easeLinear = (currntTime, startValue, byValue, duration) => {
  // easeLinear type
  // byValue = byValue(endValue - startValue)
  return startValue + (currntTime / duration) * byValue;
};

const renderAnimation = (
  data,
  textInfo,
  diffTime,
  animateTarget,
  animateValue,
) => {
  const { animation_name: animationName } = textInfo;
  if (!animationName) return;

  const animateConfig = (
    onChange,
    duration = diffTime,
    easingType = easeLinear,
  ) => {
    return {
      onChange,
      duration,
      easing: easingType,
      abort: () => !data.is_play,
    };
  };

  if (animationName === 'static') {
    setFixedAnimationStartPoint(data);

    data.fb.renderAll();

    if (data.isMultiCanvas) {
      data.fbSecond.renderAll();
    }
  }
  if (animationName === 'fade') {
    setFixedAnimationStartPoint(data);
    data.fbText.set({ opacity: animateValue.before });
    data.fbText.animate(
      animateTarget,
      animateValue.after,
      animateConfig(data.fb.renderAll.bind(data.fb)),
    );
    if (textInfo.multi_line_yn === 'Y') {
      data.fbTextSecondLine.set({ opacity: animateValue.before });
      data.fbTextSecondLine.animate(
        animateTarget,
        animateValue.after,
        animateConfig(data.fb.renderAll.bind(data.fb)),
      );
    }
    if (data.isMultiCanvas) {
      data.cloneFirstLine.set({ opacity: animateValue.before });
      data.cloneFirstLine.animate(
        animateTarget,
        animateValue.after,
        animateConfig(data.fbSecond.renderAll.bind(data.fbSecond)),
      );
      if (animationInitData.isMultiLine) {
        data.cloneSecondLine.set({ opacity: animateValue.before });
        data.cloneSecondLine.animate(
          animateTarget,
          animateValue.after,
          animateConfig(data.fbSecond.renderAll.bind(data.fbSecond)),
        );
      }
    }
  }
  if (animationName === 'scale_out') {
    setFixedAnimationStartPoint(data);
    data.fbText.set({ fontSize: 0 });
    data.fbText.animate(
      animateTarget,
      animateValue.after.first_line,
      animateConfig(data.fb.renderAll.bind(data.fb), 200),
    );
    if (textInfo.multi_line_yn === 'Y') {
      data.fbTextSecondLine.set({ fontSize: 0 });
      data.fbTextSecondLine.animate(
        animateTarget,
        animateValue.after.second_line,
        animateConfig(data.fb.renderAll.bind(data.fb), 200),
      );
    }
    if (data.isMultiCanvas) {
      data.cloneFirstLine.set({ fontSize: 0 });
      data.cloneSecondLine.set({ fontSize: 0 });
      data.cloneFirstLine.animate(
        animateTarget,
        animateValue.after.first_line,
        animateConfig(data.fbSecond.renderAll.bind(data.fbSecond), 200),
      );
      data.cloneSecondLine.animate(
        animateTarget,
        animateValue.after.second_line,
        animateConfig(data.fbSecond.renderAll.bind(data.fbSecond), 200),
      );
    }
  }
  if (animationName === 'scale_in') {
    setFixedAnimationStartPoint(data);
    data.fbText.set({ fontSize: data.fb.getHeight() / 2 });
    data.fbText.animate(
      'fontSize',
      animateValue.middle.first_line,
      animateValue.first_line_config,
    );
    if (textInfo.multi_line_yn === 'Y') {
      data.fbTextSecondLine.set({ fontSize: data.fb.getHeight() / 2 });
      data.fbTextSecondLine.animate(
        'fontSize',
        animateValue.middle.second_line,
        animateValue.second_line_config,
      );
    }
    if (data.isMultiCanvas) {
      data.cloneFirstLine.set({ fontSize: data.fb.getHeight() / 2 });
      data.cloneFirstLine.animate(
        'fontSize',
        animateValue.middle.first_line,
        animateValue.first_line_config,
      );
      if (animationInitData.isMultiLine) {
        data.cloneSecondLine.set({ fontSize: data.fb.getHeight() / 2 });
        data.cloneSecondLine.animate(
          'fontSize',
          animateValue.middle.second_line,
          animateValue.second_line_config,
        );
      }
    }
  }
  if (animationName.startsWith('drift')) {
    setMoveAnimationStartPoint(data, textInfo);
    data.fbText.animate(
      animateTarget,
      animateValue.first_line,
      animateConfig(
        data.fb.renderAll.bind(data.fb),
        500,
        fabric.util.ease.easeOutQuint,
      ),
    );
    if (textInfo.multi_line_yn === 'Y') {
      data.fbTextSecondLine.animate(
        animateTarget,
        animateValue.second_line,
        animateConfig(
          data.fb.renderAll.bind(data.fb),
          500,
          fabric.util.ease.easeOutQuint,
        ),
      );
    }
    if (data.isMultiCanvas) {
      data.cloneFirstLine.animate(
        animateTarget,
        animateValue.first_line,
        animateConfig(
          data.fbSecond.renderAll.bind(data.fbSecond),
          500,
          fabric.util.ease.easeOutQuint,
        ),
      );
      if (animationInitData.isMultiLine) {
        data.cloneSecondLine.animate(
          animateTarget,
          animateValue.second_line,
          animateConfig(
            data.fbSecond.renderAll.bind(data.fbSecond),
            500,
            fabric.util.ease.easeOutQuint,
          ),
        );
      }
    }
  }
  if (animationName.startsWith('slide')) {
    setMoveAnimationStartPoint(data, textInfo);
    data.fbText.animate(
      animateTarget,
      animateValue.first_line,
      animateConfig(data.fb.renderAll.bind(data.fb)),
    );
    if (textInfo.multi_line_yn === 'Y') {
      data.fbTextSecondLine.animate(
        animateTarget,
        animateValue.second_line,
        animateConfig(data.fb.renderAll.bind(data.fb)),
      );
    }
    if (data.isMultiCanvas) {
      data.cloneFirstLine.animate(
        animateTarget,
        animateValue.first_line,
        animateConfig(data.fbSecond.renderAll.bind(data.fbSecond)),
      );
      data.cloneSecondLine.animate(
        animateTarget,
        animateValue.second_line,
        animateConfig(data.fbSecond.renderAll.bind(data.fbSecond)),
      );
    }
  }
};

// 애니메이션 정의
const staticAnimation = (data, textInfo) => {
  animationInit(data, textInfo);
  const position = {
    first_line: animationInitData.firstPosition,
    second_line: animationInitData.secondPosition,
  };
  if (data.isVerticalAnimationCondition) {
    // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
    setAnimationPosition(position, 'center', 'top');
  } else {
    // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
    setAnimationPosition(position);
  }
  renderAnimation(data, textInfo, 0, '', null);
};

const fadeAnimation = (data, textInfo) => {
  animationInit(data, textInfo);
  const position = {
    first_line: animationInitData.firstPosition,
    second_line: animationInitData.secondPosition,
  };
  if (data.isVerticalAnimationCondition) {
    // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
    setAnimationPosition(position, 'center', 'top');
  } else {
    // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
    setAnimationPosition(position);
  }
  renderAnimation(data, textInfo, 0, 'opacity', {
    before: 0,
    after: 1,
  });
};

const slideAnimation = (data, textInfo, diffTime) => {
  animationInit(data, textInfo);
  const { font_size_first: fontSizeFirst, font_size_second: fontSizeSecond } =
    animationInitData.lineSetting;
  const slideAnimationType = textInfo.animation_name.split('_').pop();
  if (data.isVerticalAnimationCondition) {
    animationInitData.fontSizeFirstLine = fontSizeFirst;
    animationInitData.fontSizeSecondLine = fontSizeSecond;
  } else {
    animationInitData.fontSizeFirstLine = data.fbText.getScaledHeight();
    animationInitData.fontSizeSecondLine =
      data.fbTextSecondLine.getScaledHeight();
  }

  const firstWidth = data.fbText.width;
  const secondWidth = data.fbTextSecondLine.width;

  switch (slideAnimationType) {
    case 'top': {
      if (data.isVerticalAnimationCondition) {
        animationStartPosition = {
          first_line: {
            top: data.fb.getHeight(),
            left: animationInitData.firstPosition.left,
          },
          second_line: {
            top: data.fb.getHeight(),
            left: animationInitData.secondPosition.left,
          },
        };
      } else {
        animationStartPosition = {
          first_line: {
            top: data.fb.getHeight() + animationInitData.fontSizeFirstLine,
            left: animationInitData.firstPosition.left,
          },
          second_line: {
            top:
              data.fb.getHeight() +
              animationInitData.fontSizeFirstLine +
              animationInitData.topDiff,
            left: animationInitData.secondPosition.left,
          },
        };
      }
      if (data.isVerticalAnimationCondition) {
        if (textInfo.multi_line_yn === 'Y') {
          const slideUpEndHeight =
            data.fbText.height <= data.fbTextSecondLine.height
              ? data.fbText.height
              : data.fbTextSecondLine.height;
          animationEndPosition = {
            first_line: -slideUpEndHeight,
            second_line: -slideUpEndHeight,
          };
        } else {
          animationEndPosition = {
            first_line: -data.fbText.height,
            second_line: 0,
          };
        }
      } else if (!data.isVerticalAnimationCondition) {
        if (textInfo.multi_line_yn === 'Y') {
          animationEndPosition = {
            first_line:
              -animationInitData.fontSizeSecondLine - animationInitData.topDiff,
            second_line: -animationInitData.fontSizeSecondLine,
          };
        } else {
          animationEndPosition = {
            first_line: -animationInitData.fontSizeFirstLine,
            second_line: 0,
          };
        }
      }
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition);
      }
      renderAnimation(data, textInfo, diffTime, 'top', animationEndPosition);
      break;
    }
    case 'bottom': {
      if (data.isVerticalAnimationCondition) {
        const bottomStartTop =
          data.isShowTab === true
            ? -data.fb.getHeight()
            : -(data.fb.getHeight() * 2 + animationInitData.fontSizeFirstLine);
        animationStartPosition = {
          first_line: {
            top: bottomStartTop,
            left: animationInitData.firstPosition.left,
          },
          second_line: {
            top: bottomStartTop,
            left: animationInitData.secondPosition.left,
          },
        };
        if (textInfo.multi_line_yn !== 'Y') {
          animationStartPosition.first_line.top =
            data.product_type_id === 'V044391'
              ? -(data.fb.getHeight() * 3)
              : -(data.fb.getHeight() * 2);
          animationStartPosition.second_line.top = 0;
        }
      } else if (!data.isVerticalAnimationCondition) {
        if (textInfo.multi_line_yn === 'Y') {
          animationStartPosition = {
            first_line: {
              top:
                -animationInitData.fontSizeSecondLine -
                animationInitData.topDiff,
              left: animationInitData.firstPosition.left,
            },
            second_line: {
              top: -animationInitData.fontSizeSecondLine,
              left: animationInitData.secondPosition.left,
            },
          };
        } else {
          animationStartPosition = {
            first_line: {
              top: -animationInitData.fontSizeFirstLine,
              left: animationInitData.firstPosition.left,
            },
            second_line: {
              top: 0,
              left: animationInitData.secondPosition.left,
            },
          };
        }
      }
      if (data.isVerticalAnimationCondition) {
        animationEndPosition = {
          first_line: data.fb.getHeight() + 10,
          second_line: data.fb.getHeight() + 10,
        };
      } else {
        animationEndPosition = {
          first_line:
            data.fb.getHeight() + animationInitData.fontSizeFirstLine / 2,
          second_line:
            data.fb.getHeight() +
            animationInitData.topDiff +
            animationInitData.fontSizeFirstLine / 2,
        };
      }

      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition);
      }
      renderAnimation(data, textInfo, diffTime, 'top', animationEndPosition);
      break;
    }
    case 'left': {
      if (firstWidth >= secondWidth) {
        // 첫번째 줄이 두번째 줄보다 길거나 같은경우 (두 줄이 중앙정렬되도록 left값 조정)
        animationStartPosition = {
          first_line: {
            top: animationInitData.firstPosition.top,
            left: data.fb.getWidth(),
          },
          second_line: {
            top: animationInitData.secondPosition.top,
            left: data.fb.getWidth() + (firstWidth - secondWidth) / 2,
          },
        };
      } else {
        // 두번째 줄이 첫번째 줄보다 길거나 같은경우 (두 줄이 중앙정렬되도록 left값 조정)
        animationStartPosition = {
          first_line: {
            top: animationInitData.firstPosition.top,
            left: data.fb.getWidth() + (secondWidth - firstWidth) / 2,
          },
          second_line: {
            top: animationInitData.secondPosition.top,
            left: data.fb.getWidth(),
          },
        };
      }
      if (firstWidth >= secondWidth) {
        animationEndPosition = {
          first_line: -firstWidth,
          second_line: -(secondWidth + (firstWidth - secondWidth) / 2),
        };
      } else {
        animationEndPosition = {
          first_line: -(firstWidth + (secondWidth - firstWidth) / 2),
          second_line: -secondWidth,
        };
      }

      animationEndPosition.first_line -= Math.floor(firstWidth * 0.1);
      animationEndPosition.second_line -= Math.floor(secondWidth * 0.1);

      if (data.isVerticalAnimationCondition) {
        // 1x1세로 전광판인 경우
        animationStartPosition = {
          first_line: {
            top: animationInitData.firstPosition.top,
            left: data.fb.getWidth(),
          },
          second_line: {
            top: animationInitData.secondPosition.top,
            left: data.fb.getWidth() + animationInitData.leftDiff,
          },
        };
        animationEndPosition = {
          first_line: -(
            animationInitData.fontSizeFirstLine * 2 +
            animationInitData.leftDiff
          ),
          second_line: -(animationInitData.fontSizeSecondLine * 2),
        };
      }
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'left', 'top');
      } else {
        if (data.product_type_id === 'S063391') {
          animationStartPosition.first_line.top *= 0.95;
          animationStartPosition.second_line.top *= 1.05;
        }

        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition, 'left');
      }
      renderAnimation(data, textInfo, diffTime, 'left', animationEndPosition);
      break;
    }
    case 'right': {
      if (firstWidth >= secondWidth) {
        animationStartPosition = {
          first_line: {
            top: animationInitData.firstPosition.top,
            left: -firstWidth,
          },
          second_line: {
            top: animationInitData.secondPosition.top,
            left: -(secondWidth + (firstWidth - secondWidth) / 2),
          },
        };
      } else {
        animationStartPosition = {
          first_line: {
            top: animationInitData.firstPosition.top,
            left: -(firstWidth + (secondWidth - firstWidth) / 2),
          },
          second_line: {
            top: animationInitData.secondPosition.top,
            left: -secondWidth,
          },
        };
      }

      if (firstWidth > secondWidth) {
        animationEndPosition = {
          first_line: data.fb.getWidth(),
          second_line: data.fb.getWidth() + (firstWidth - secondWidth) / 2,
        };
      } else {
        animationEndPosition = {
          first_line: data.fb.getWidth() + (secondWidth - firstWidth) / 2,
          second_line: data.fb.getWidth(),
        };
      }

      animationEndPosition.first_line += Math.floor(firstWidth * 0.1);
      animationEndPosition.second_line += Math.floor(secondWidth * 0.1);

      if (data.isVerticalAnimationCondition) {
        animationStartPosition = {
          first_line: {
            top: animationInitData.firstPosition.top,
            left: -(
              animationInitData.leftDiff + animationInitData.fontSizeFirstLine
            ),
          },
          second_line: {
            top: animationInitData.firstPosition.top,
            left: -animationInitData.fontSizeSecondLine,
          },
        };
        const rangeFix = textInfo.text_size_type === 'small' ? 10 : 20;
        animationEndPosition = {
          first_line: data.fb.getWidth() + rangeFix,
          second_line:
            data.fb.getWidth() + animationInitData.leftDiff + rangeFix,
        };
      }
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'left', 'top');
      } else {
        if (data.product_type_id === 'S063391') {
          animationStartPosition.first_line.top *= 0.95;
          animationStartPosition.second_line.top *= 1.05;
        }

        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition, 'left');
      }
      renderAnimation(data, textInfo, diffTime, 'left', animationEndPosition);
      break;
    }
    default:
      break;
  }
};

const driftAnimation = (data, textInfo, diffTime) => {
  animationInit(data, textInfo);
  const longText =
    data.fbText.width > data.fbTextSecondLine.width
      ? data.fbText.width
      : data.fbTextSecondLine.width;
  const driftAnimationType = textInfo.animation_name.split('_').pop();

  const topEnd = {
    first_line: animationInitData.firstPosition.top,
    second_line: animationInitData.secondPosition.top,
  };
  const leftEnd = {
    first_line: animationInitData.firstPosition.left,
    second_line: animationInitData.secondPosition.left,
  };
  switch (driftAnimationType) {
    case 'top':
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(
          {
            first_line: {
              top: data.fb.getHeight(),
              left: animationInitData.firstPosition.left,
            },
            second_line: {
              top: data.fb.getHeight(),
              left: animationInitData.secondPosition.left,
            },
          },
          'center',
          'top',
        );
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition({
          first_line: {
            top: data.fb.getHeight(),
            left: animationInitData.firstPosition.left,
          },
          second_line: {
            top: data.fb.getHeight() + animationInitData.topDiff,
            left: animationInitData.secondPosition.left,
          },
        });
      }
      renderAnimation(data, textInfo, diffTime, 'top', topEnd);
      break;
    case 'bottom': {
      if (data.isVerticalAnimationCondition || data.isBannerCondition) {
        animationStartPosition = {
          first_line: {
            top: -data.fb.getHeight(),
            left: animationInitData.firstPosition.left,
          },
          second_line: {
            top: -data.fb.getHeight(),
            left: animationInitData.secondPosition.left,
          },
        };
      } else {
        animationStartPosition = {
          first_line: {
            top: -(
              animationInitData.fontSizeFirstLine + animationInitData.topDiff
            ),
            left: animationInitData.firstPosition.left,
          },
          second_line: {
            top: -animationInitData.fontSizeSecondLine,
            left: animationInitData.secondPosition.left,
          },
        };
      }
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition);
      }
      renderAnimation(data, textInfo, diffTime, 'top', topEnd);
      break;
    }
    case 'left': {
      animationStartPosition = {
        first_line: {
          top: animationInitData.firstPosition.top,
          left: data.fb.getWidth() + longText,
        },
        second_line: {
          top: animationInitData.secondPosition.top,
          left: data.fb.getWidth() + longText,
        },
      };
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition);
      }
      renderAnimation(data, textInfo, diffTime, 'left', leftEnd);
      break;
    }
    case 'right': {
      animationStartPosition = {
        first_line: {
          top: animationInitData.firstPosition.top,
          left: -(data.fb.getWidth() + longText),
        },
        second_line: {
          top: animationInitData.secondPosition.top,
          left: -(data.fb.getWidth() + longText),
        },
      };
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(animationStartPosition, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(animationStartPosition);
      }
      renderAnimation(data, textInfo, diffTime, 'left', leftEnd);
      break;
    }
    default:
      break;
  }
};

const scaleAnimation = (data, textInfo, diffTime) => {
  animationInit(data, textInfo);

  const position = {
    first_line: animationInitData.firstPosition,
    second_line: animationInitData.secondPosition,
  };
  const scaleAnimationType = textInfo.animation_name.split('_').pop();
  switch (scaleAnimationType) {
    case 'in': {
      const scaleInValue = (onChange, firstLine, secondLine) => {
        return {
          before: data.fb.getHeight() / 2,
          middle: {
            first_line: animationInitData.fontSizeFirstLine,
            second_line: animationInitData.fontSizeSecondLine,
          },
          after: 0,
          first_line_config: {
            onChange,
            duration: 100,
            onComplete: () => {
              setTimeout(() => {
                data[firstLine].animate('fontSize', 0, {
                  onChange,
                  duration: 200,
                });
              }, 4550);
            },
          },
          second_line_config: {
            onChange,
            duration: 100,
            onComplete: () => {
              setTimeout(() => {
                data[secondLine].animate('fontSize', 0, {
                  onChange,
                  duration: 200,
                });
              }, 4550);
            },
          },
        };
      };
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(position, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(position);
      }
      renderAnimation(
        data,
        textInfo,
        diffTime,
        'fontSize',
        scaleInValue(
          data.fb.renderAll.bind(data.fb),
          'fbText',
          'fbTextSecondLine',
        ),
      );
      if (data.isMultiCanvas) {
        renderAnimation(
          data,
          textInfo,
          diffTime,
          'fontSize',
          scaleInValue(
            data.fbSecond.renderAll.bind(data.fbSecond),
            'cloneFirstLine',
            'cloneSecondLine',
          ),
        );
      }
      break;
    }
    case 'out': {
      const scaleOutValue = {
        before: 0,
        middle: 10,
        after: {
          first_line: animationInitData.fontSizeFirstLine,
          second_line: animationInitData.fontSizeSecondLine,
        },
      };
      if (data.isVerticalAnimationCondition) {
        // 1x1세로유형 전광판인경우 세로 기준점을 top으로 지정
        setAnimationPosition(position, 'center', 'top');
      } else {
        // 기타 전광판인경우 가로/세로 기준점 모두 center로 지정
        setAnimationPosition(position);
      }
      renderAnimation(data, textInfo, diffTime, 'fontSize', scaleOutValue);
      break;
    }
    default:
      break;
  }
};

export default {
  animationInit,
  slideAnimation,
  staticAnimation,
  fadeAnimation,
  driftAnimation,
  scaleAnimation,
};
